import React from "react";
import { pickDataAttributes } from "../data-attributes";
import { CONTROL_ACTIONS, type ControlAction } from "../control-action.constants";
import { plainText, richText } from "../text/text";
import styles from "./link.module.css";
import { LINK_SCHEMES, safeHref } from "./link.href";
import { withSafeRender } from "../safe-render";

/**
 * Link. Server Component: an `<a>`, no event handlers. GTM's Link Click
 * trigger is gated on `data-linkposition` / `-linkdestination` — the trigger
 * reports a click only on an element (or nearest attributed ancestor)
 * carrying at least two of placement, name and destination, so a handler or
 * `"use client"` here would double-count a click that is otherwise only
 * reported once, through the attributes. ⚠ The click's NAME comes from the
 * element's own text, never from `data-linkname`, which leaves an icon-only
 * link reporting an empty name. Details: `design-conflicts/link.mdx`.
 */
export type LinkContrast = "low" | "high";
export type LinkSize = "default" | "small";
export type LinkMode = "dark" | "light";
/** What activating the link does: navigate to `href`, or open the page's iframe
 *  modal. Button shares this vocabulary — defined once in `control-action`. */
export type LinkAction = ControlAction;

const CONTRASTS: Record<LinkContrast, true> = {
  low: true,
  high: true
};

const SIZES: Record<LinkSize, true> = {
  default: true,
  small: true
};

/* The published link actions — the shared set under the link's own name, so a
   CMS string can never reach the DOM as a bogus value (anything off it falls
   back to `redirect`). Not JSDoc: Storybook's docgen reads a documented export
   as a component and adds keys to the object, which here is the shared set. */
export const LINK_ACTIONS = CONTROL_ACTIONS;

/** The value if the set lists it, the fallback if not, so a CMS string can
 *  never reach a same-named class in the module map. `hasOwnProperty` rather
 *  than a bracket lookup, which would admit `constructor` and friends. */
function published<T extends string>(
  set: Record<T, true>,
  value: string,
  fallback: T
): T {
  return Object.prototype.hasOwnProperty.call(set, value) ? (value as T) : fallback;
}

export { LINK_SCHEMES, safeHref };

/* Appended to `ariaLabel` where there is one — a label replaces the subtree, so
   hidden text in an icon-only anchor is never announced — and `.dds-sr-only`
   text otherwise, so visible wording stays in the name (SC 2.5.3). */
const NEW_WINDOW_CUE = "(opens in a new window)";

/* Design-system chevrons, painted by the atom when startIcon / endIcon is
   set to `true`. Inlined here so a Storyblok Boolean switch produces the
   arrow without a developer wiring an SVG at the call site — DDS has no
   icon atom yet, and the two shapes are the only ones the Link frame
   publishes.

   `stroke="currentColor"` and NO width / height on the SVG so the icon
   inherits its size from the parent `.icon` slot (16px on Default, 12px on
   Small) and its colour from the anchor's `color`. Both matter: the size
   makes Small vs Default work without extra rules; the colour makes hover
   / pressed / visited / disabled step alongside the label instead of
   staying frozen at one hue. Both features are only available because the
   SVG is rendered inline as JSX rather than through `<img src>`. */
const ChevronLeft = () => (
  <svg fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15 6l-6 6 6 6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const ChevronRight = () => (
  <svg fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

/**
 * True when nothing in `children` can announce anything — dev diagnostic only,
 * and not `hasLabel`: that asks whether a label was supplied, this whether one
 * will be heard.
 */
function announcesNothing(children: React.ReactNode): boolean {
  return React.Children.toArray(children).every(child => {
    if (typeof child === "string" || typeof child === "number") {
      return String(child).trim() === "";
    }
    if (React.isValidElement(child) && child.type === React.Fragment) {
      return announcesNothing((child.props as { children?: React.ReactNode }).children);
    }
    return false;
  });
}

/**
 * Reporting props for the GTM Link Click trigger. Opt-in in two halves; nothing
 * reports by default, because an `href` can hold an order id or a token.
 * `linkPosition` is case-sensitive in GTM and this atom renders it VERBATIM —
 * nothing here lower-cases it, so whatever the caller passes is the placement
 * reported. Capitalised values are not only hand-typed ones: `organisms/footer`
 * passes `LINK_POSITION_FOOTER` (`"Footer"`) into this atom's `linkPosition`, a
 * capital kept on purpose — see `services/analytics` — so do not "correct" it
 * on the way through.
 */
export interface LinkTrackingProps {
  /** Names the click in reporting, as `data-linkname`. Not derived from
   *  `children` or `ariaLabel` — both carry the new-window cue, which must not
   *  reach reporting. ⚠ The container does not read this today; it names the
   *  click from the element's own text. Still rendered, so the attribute is
   *  there the day it does. */
  linkName?: string;
  /** Renders as `data-linkposition` for the GTM trigger to read. */
  linkPosition?: string;
  /** Renders as `data-linkdestination`. Defaults to `href` when `linkPosition`
   *  is set and this is not; setting it opts the link in on its own. */
  linkDestination?: string;
}

interface LinkBaseProps extends LinkTrackingProps {
  /** How much the link stands out against the surface — Figma's `Contrast`
   *  property. `low` resolves to `color-link-tertiary` (`--blue-300`,
   *  `#5da3fc`) — the primary-action blue. `high` resolves to
   *  `color-link-primary` (`--slate-50`, `#f4f7fc`) — the body-text colour
   *  that reads as understated inside a paragraph. */
  contrast?: LinkContrast;
  size?: LinkSize;
  /** Which surface the link sits on. Dark is the default; light for a light background. */
  mode?: LinkMode;
  /** Where the link points. A destination the allowlist refuses renders as "#". */
  href: string;
  /** Greys the link out, drops the destination and announces `unavailable`. */
  disabled?: boolean;
  /** Opens the destination in a new tab, and says so in the spoken name. */
  newWindow?: boolean;
  /** Forces the "visited" colour; browsers apply `:visited` on their own. */
  visited?: boolean;
  /** Two modes, one prop:
   *  - `true` — paint the design-system chevron in that slot. The Storyblok
   *    Boolean field lands here; a CMS author flips a switch. Inlined as JSX,
   *    so the arrow inherits the surrounding label's colour on every state
   *    (Low / High contrast, hover / pressed / visited / disabled) and size.
   *  - a React element — render that element in the icon slot instead. Lets
   *    a caller supply a bespoke glyph (`organisms/footer` passes an `<img>`
   *    per social row). Nothing forced on it; a raster or an SVG will not
   *    follow the label's colour on state changes the way the chevron does.
   *  Anything else — including the string `"false"` Storyblok publishes for
   *  an unset switch — paints nothing. Both slots are decorative and
   *  rendered `aria-hidden`. */
  startIcon?: boolean | React.ReactElement;
  endIcon?: boolean | React.ReactElement;
  /** DOM `id` — descriptive and hyphenated, so tag-manager rules can find it. */
  id?: string;
  /**
   * What activating the link does. `redirect` (the default) navigates to `href`.
   * `iframe_modal` makes the link a trigger for the page's iframe modal: it
   * renders `data-iframe-modal`, so the page's `IframeModal` opens embedding this
   * link's `href` — which stays the no-JS fallback. `legal_modal` does the same
   * for the page's `LegalModal`, rendering `data-legal-modal` — but that one
   * FETCHES the `href` and renders the document, so the href is a legal
   * fragment rather than a page. Each needs its own modal on the page; see
   * `organisms/iframe-modal` and `organisms/legal-modal`. Anything off the set
   * falls back to `redirect`.
   */
  linkAction?: LinkAction;
  /**
   * Declarative `data-*` hooks to render on the anchor — e.g.
   * `{ "data-iframe-src": "/x" }` to embed a url other than `href` in the iframe
   * modal. ⚠ `data-*` keys only; a `className`, handler or `aria-*` passed here
   * is dropped, and it cannot overwrite the link's own reporting attributes or
   * the `data-iframe-modal` that `linkAction="iframe_modal"` sets.
   */
  dataAttributes?: Record<string, string>;
  className?: string;
}

/**
 * A link must announce something: with visible wording `ariaLabel` is optional
 * and a redundant one only duplicates it; with none, it is the only name there
 * is and is required.
 */
export type LinkProps = LinkBaseProps &
  (
    | {
        children: React.ReactNode;
        /** Read aloud instead of the wording, when the wording is not descriptive. */
        ariaLabel?: string;
      }
    | {
        children?: never;
        /** Required: with no wording, this is the link's only accessible name. */
        ariaLabel: string;
      }
  );

function LinkImpl({
  contrast = "low",
  size = "default",
  mode = "dark",
  href,
  disabled = false,
  newWindow = false,
  visited = false,
  startIcon,
  endIcon,
  ariaLabel,
  id,
  linkAction,
  dataAttributes,
  className,
  children,
  linkName,
  linkPosition,
  linkDestination
}: LinkProps) {
  const resolvedContrast = published(CONTRASTS, contrast, "low");
  const resolvedSize = published(SIZES, size, "default");

  const safe = safeHref(href);

  /* `=== true` on every switch field (disabled, newWindow, visited, and the
     two icon slots below): Storyblok publishes an unset switch as the string
     "false", which is truthy. */
  const isDisabled = disabled === true;
  const opensNewWindow = newWindow === true && !isDisabled;

  /* Only an explicitly absent label counts as no label — `!children` would
     misfire on `children={0}`, which is wording a CMS can send. */
  const hasLabel = !(
    children === undefined ||
    children === null ||
    children === false ||
    children === ""
  );

  /* The type still admits `<Link>{ctaLabel && ctaLabel}</Link>` — children that
     announce nothing (WCAG 4.1.2, 2.4.4). `ariaLabel` is trimmed so both halves
     agree on what "empty" means. Dev-only, compiled out of production. */
  if (process.env.NODE_ENV !== "production" && !ariaLabel?.trim() && announcesNothing(children)) {
    console.error(
      "[Link] renders no wording and has no ariaLabel, so it has no accessible name. Give it wording, or an ariaLabel describing what it does."
    );
  }

  const classes = [
    styles.link,
    styles[resolvedContrast],
    styles[resolvedSize],
    // Dark is the base ruleset, so it needs no class; light layers over it.
    mode === "light" ? styles.light : null,
    className
  ]
    .filter(Boolean)
    .join(" ");

  /* Gated on `linkPosition || linkDestination`, not on the resolved value:
     `linkDestination || safe` is always truthy, so gating on that would put
     `data-linkdestination` — raw query string and all — on every link. */
  const trackingAttrs: {
    "data-linkname"?: string;
    "data-linkposition"?: string;
    "data-linkdestination"?: string;
  } = {};

  /* Trimmed, and the gate reads the trimmed value: whitespace is invisible in
     the feed and splits one figure into two rows in GTM. `plainText` runs
     first, the same reduction an accessible name takes, so a name authored as
     `<sup>®</sup>` is filed under the wording the reader sees rather than
     carrying raw tags into reporting — and a name that is nothing but markup
     reduces to "" and fails the gate below, which is the honest answer.

     ⚠ THE SECOND TRIM IS NOT REDUNDANT. `plainText` collapses HTML whitespace
     only — `[ \t\n\r\f]` — and deliberately KEEPS a leading or trailing
     no-break space, because one is a character the copy chose. GTM has no such
     opinion and files a name opening on `&nbsp;` as a row of its own, so
     the reporting key takes JS `trim()` - which does strip it - on top. */
  const reportedName = typeof linkName === "string" ? plainText(linkName).trim() : "";
  if (reportedName) {
    trackingAttrs["data-linkname"] = reportedName;
  }

  if (linkPosition || linkDestination) {
    if (linkPosition) {
      trackingAttrs["data-linkposition"] = linkPosition;
    }
    /* The sanitised href, not the raw one — a refused destination is a
       navigation that never happened. */
    trackingAttrs["data-linkdestination"] = linkDestination || safe;
  }

  /* Two acceptable shapes, checked in this order: `=== true` paints the atom's
     own chevron; a valid React element renders unchanged in the slot. Anything
     else — including the string `"false"` Storyblok publishes for an unset
     switch, and every other truthy non-element — paints nothing. Slots are
     decorative; outer span is `aria-hidden`. */
  const startIconNode = React.isValidElement(startIcon) ? startIcon : null;
  const endIconNode = React.isValidElement(endIcon) ? endIcon : null;
  const showStartChevron = startIcon === true;
  const showEndChevron = endIcon === true;

  /* `iframe_modal` renders the trigger attribute the page's IframeModal listens
     for, embedding this link's own href — validated against the set so a bogus
     CMS string falls back to a redirect, and GATED on a real destination: with
     no href (`safe` is "#") and no explicit `data-iframe-src`, the frame would
     resolve "#" to THIS page and embed it in itself, so the trigger is withheld.
     The action key is merged LAST so a caller's `{ "data-iframe-modal": undefined }`
     cannot strip it (overriding the embedded url via `data-iframe-src` still works
     — different key); `dataProps` is then spread FIRST on the anchor, so href, id
     and the trackingAttrs data-link* pair win over the caller's map. */
  const resolvedAction = published<LinkAction>(LINK_ACTIONS, linkAction ?? "redirect", "redirect");
  const opensIframeModal =
    resolvedAction === "iframe_modal" &&
    (safe !== "#" || dataAttributes?.["data-iframe-src"] !== undefined);
  /* The LegalModal fetches the document at this link's own href, so the trigger
     needs a real destination and takes no src override of its own. */
  const opensLegalModal = resolvedAction === "legal_modal" && safe !== "#";
  const dataProps = pickDataAttributes({
    ...dataAttributes,
    ...(opensIframeModal ? { "data-iframe-modal": "" } : {}),
    ...(opensLegalModal ? { "data-legal-modal": "" } : {})
  });

  const content = (
    <>
      {(showStartChevron || startIconNode) && (
        <span aria-hidden="true" className={styles.icon}>
          {startIconNode ?? <ChevronLeft />}
        </span>
      )}
      {/* A STRING ONLY goes through the parse Text uses, so an authored
          `<sup>®</sup>` draws the element rather than printing its tags. Every
          other node type is already React's — a call site passing an element,
          a fragment or a number renders exactly what it did before, and the
          span itself is unchanged either way. */}
      {hasLabel && (
        <span className={styles.label}>
          {typeof children === "string" ? richText(children) : children}
        </span>
      )}
      {(showEndChevron || endIconNode) && (
        <span aria-hidden="true" className={styles.icon}>
          {endIconNode ?? <ChevronRight />}
        </span>
      )}
      {opensNewWindow && !ariaLabel && (
        <span className="dds-sr-only">{NEW_WINDOW_CUE}</span>
      )}
    </>
  );

  /* A `<span role="link">` rather than a dead `<a>`: GTM's trigger fires on any
     anchor click, so a disabled anchor would still report an empty `link_click`.
     `tabIndex` keeps the state reachable enough to be announced. No
     `data-visited` — every visited rule is guarded against `aria-disabled`. */
  if (isDisabled) {
    return (
      <span
        aria-disabled="true"
        aria-label={ariaLabel}
        className={classes}
        id={id}
        role="link"
        tabIndex={0}
      >
        {content}
      </span>
    );
  }

  return (
    <a
      {...dataProps}
      aria-label={
        opensNewWindow && ariaLabel ? `${ariaLabel} ${NEW_WINDOW_CUE}` : ariaLabel
      }
      className={classes}
      // Lets Storybook and demo pages force the state without a history entry.
      data-visited={visited === true || undefined}
      href={safe}
      id={id}
      // Without `noopener` the opened document reaches back through `window.opener`.
      rel={opensNewWindow ? "noopener noreferrer" : undefined}
      target={opensNewWindow ? "_blank" : undefined}
      {...trackingAttrs}
    >
      {content}
    </a>
  );
}
export const Link = withSafeRender(LinkImpl, "Link");

export default Link;
